import { NgIf } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
  FuseHorizontalNavigationComponent,
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { TranslocoModule } from '@ngneat/transloco';
import { PermissionsEnum } from '@shared/features/user/user-roles.interface';
import { MatMenuModule } from '@angular/material/menu';
import { RequestsApiService } from '@features/requests/services/requests-api.service';
import { IRequestsCount, RequestStatusEnum } from '@features/requests/models/requests';
import { cloneDeep } from 'lodash-es';
import { RequestsService } from '@features/requests/services/requests.service';
import { IUser } from '@shared/features/user/user.interface';
import { UserService } from '@shared/features/user/user.service';
import { ApplicationApiService } from '@features/applications/services/application-api.service';
import { APPLICATION_STATUSES } from '@features/applications/constants/application-status.constant';

export const scrolled$ = new BehaviorSubject<Event | null>(null);

@Component({
  selector: 'enterprise-layout',
  templateUrl: './enterprise.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    FuseLoadingBarComponent,
    NgIf,
    FuseVerticalNavigationComponent,
    MatButtonModule,
    MatIconModule,
    LanguagesComponent,
    UserComponent,
    FuseHorizontalNavigationComponent,
    RouterOutlet,
    TranslocoModule,
    MatMenuModule,
    RouterLink,
  ],
})
export class EnterpriseLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;
  navigation: Navigation;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  user: IUser;
  readonly logoSrc = 'assets/icons/logo-original.svg';
  readonly permissions = PermissionsEnum;
  /**
   * Constructor
   */
  constructor(
    private _router: Router,
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private requestsApiService: RequestsApiService,
    private requestsService: RequestsService,
    private _userService: UserService,
    private applicationApiService: ApplicationApiService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.user = this._userService.user;
    // Subscribe to navigation data
    this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
      this.navigation = navigation;
    });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });
    this.listenForUpdates();
    this.requestsService.listenOnUpdate.next(true);
  }

  listenForUpdates() {
    this.requestsService.listenOnUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(shouldUpdate => {
      if (shouldUpdate) {
        this.getApplicationsCounts(this.user?.brigade?.complex?.id);
        this.getRequestsCounts();
      }
    });
  }

  getRequestsCounts() {
    if (!this.user?.brigade?.complex?.id) return;
    this.requestsApiService
      .getRequestCount<IRequestsCount>({ complex_id: this.user?.brigade?.complex?.id })
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((responses: IRequestsCount) => {
        const allCounts = responses[RequestStatusEnum.PENDING] || 0;
        this.navigation = cloneDeep({
          ...this.navigation,
          default: this.navigation.default.map(item => {
            if (item.id === 'requests') {
              item.badge = {
                title: allCounts ? String(allCounts) : '0',
                classes: 'rounded-xl bg-orange-500 px-1.5 text-white',
              };
            }
            return item;
          }),
        });
      });
  }

  getApplicationsCounts(complex_id?: string) {
    let params: any = {};
    if (!!complex_id) {
      params.complex_id = complex_id;
    }
    this.applicationApiService
      .getApplicationCounts(params)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(responses => {
        const allCounts = responses?.find(count => count.status === APPLICATION_STATUSES.CREATED)?.count || 0;
        this.navigation = cloneDeep({
          ...this.navigation,
          default: this.navigation.default.map(item => {
            if (item.id === 'applications') {
              item.badge = {
                title: allCounts ? String(allCounts) : '0',
                classes: 'rounded-xl bg-orange-500 px-1.5 text-white',
              };
            }
            return item;
          }),
        });
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  @HostListener('scroll', ['$event'])
  scroll(e: Event) {
    scrolled$.next(e);
  }

  onCreate() {
    this._router.navigate(['incident', 'create']);
  }

  hasPermission(permission: PermissionsEnum): boolean {
    return !!this.user?.roles?.some(r => r.code === permission);
  }
}
